import * as Label from '@radix-ui/react-label';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useToggle } from 'react-use';
import { SellerAccountEmployeeMultiSelector } from 'src/components/Selectors/SellerAccountEmployeeSelector';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { FailureMessage } from 'src/core/POS/MessageWithIcon';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosTextArea } from 'src/core/POS/PosTextArea';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { Button, Stack } from 'src/core/ui';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'src/modals/Modal';
import { EMPTY_GUID } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { EVENT_NOTIFICATION_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { isSuccess } from 'src/utils/errorUtils';
import { normalizeWebhookUrl } from 'src/utils/stringUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  EventDiscoveryClient,
  EventNotification,
  EventNotificationType,
  Webhook,
} from 'src/WebApiController';

import { useGetFilteredNotifications } from './hooks/useGetFilteredNotifications';
import * as styles from './NotificationModal.css';

export const NotificationModalHeader = ({
  isEditMode,
}: {
  isEditMode: boolean;
}) => {
  return (
    <div className={styles.header}>
      <h5 className={styles.headerText}>
        <Content
          id={
            isEditMode
              ? ContentId.EditNotification
              : ContentId.CreateNotification
          }
        />
      </h5>
    </div>
  );
};

type NotificationModalFooterProps = {
  isEditMode: boolean;
  onClose: () => void;
};

export const NotificationModalFooter = ({
  onClose,
  isEditMode,
}: NotificationModalFooterProps) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const { handleSubmit, setError, getValues, setValue, clearErrors } =
    useFormContext<EventNotification>();
  const requiredMsg = useContent(ContentId.Required);
  const [isLoading, setIsLoading] = useState(false);
  const { notificationsQuery } = useGetFilteredNotifications({
    searchText: '',
  });

  const onSubmit = useCallback(
    async (formValue: EventNotification) => {
      setIsLoading(true);
      tryInvokeApi(
        async () => {
          const client = await new EventDiscoveryClient(
            activeAccountWebClientConfig
          );

          let isSucceeded = false;
          if (isEditMode) {
            const result = await client.updateEventNotification(formValue);

            if (isSuccess(result)) {
              isSucceeded = true;
            }
          } else {
            const newNotification =
              await client.createEventNotification(formValue);
            isSucceeded = newNotification != null;
          }

          if (isSucceeded) {
            notificationsQuery.refetch();
            onClose();
          }
        },
        (error) => {
          showErrorDialog(
            isEditMode
              ? 'EventDiscoveryClient.updateEventNotification'
              : 'EventDiscoveryClient.createEventNotification',
            error,
            {
              trackErrorData: formValue,
            }
          );
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      isEditMode,
      notificationsQuery,
      onClose,
      showErrorDialog,
    ]
  );

  const onSubmitHandler = useCallback(() => {
    const formValue = getValues();
    const { name, notificationType, filters, userIdTargets, webhookTargets } =
      formValue;

    clearErrors();
    let hasErrors = false;

    if (!name) {
      setError('name', { message: requiredMsg });
      hasErrors = true;
    }

    if (!notificationType) {
      setError('notificationType', { message: requiredMsg });
      hasErrors = true;
    }

    if (notificationType === EventNotificationType.Webhook) {
      if (!webhookTargets || !webhookTargets.length || !webhookTargets[0]) {
        setError('webhookTargets', { message: requiredMsg });
        hasErrors = true;
      } else {
        webhookTargets.forEach((target, index) => {
          if (target.url.trim() === '') {
            setError(`webhookTargets.${index}.url`, { message: requiredMsg });
            hasErrors = true;
          } else {
            const normalizedUrl = normalizeWebhookUrl(target.url);
            if (!normalizedUrl) {
              setError(`webhookTargets.${index}.url`, {
                message: 'Invalid url format',
              });
              hasErrors = true;
            } else if (normalizedUrl != target.url) {
              // If the URL was normalized, update the form value
              setValue(`webhookTargets.${index}.url`, normalizedUrl);
            }
          }
        });
      }
    }

    if (notificationType === EventNotificationType.Email) {
      if (!userIdTargets || !userIdTargets.length || !userIdTargets[0]) {
        setError('userIdTargets', { message: requiredMsg });
        hasErrors = true;
      }
    }

    if (!filters) {
      setError('filters', { message: requiredMsg });
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    handleSubmit(onSubmit)();
  }, [
    getValues,
    clearErrors,
    handleSubmit,
    onSubmit,
    setError,
    requiredMsg,
    setValue,
  ]);

  const onDelete = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      setIsLoading(true);
      tryInvokeApi(
        async () => {
          const client = await new EventDiscoveryClient(
            activeAccountWebClientConfig
          );
          // Get current notification id/value
          const notification = getValues();
          if (!notification?.id) {
            setIsLoading(false);
            showErrorDialog(
              'Delete Notification',
              new Error('Notification ID is missing')
            );
            return;
          }
          const result = await client.deleteEventNotification(notification.id);
          if (isSuccess(result)) {
            notificationsQuery.refetch();
            onClose();
          } else {
            showErrorDialog(
              'EventDiscoveryClient.deleteEventNotification',
              result,
              {
                trackErrorData: notification,
              }
            );
          }
        },
        (error) => {
          showErrorDialog(
            'EventDiscoveryClient.deleteEventNotification',
            error,
            {
              trackErrorData: getValues(),
            }
          );
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      getValues,
      notificationsQuery,
      onClose,
      showErrorDialog,
    ]
  );

  return (
    <Stack width="full" justifyContent="spaceBetween">
      {isEditMode ? (
        <Stack gap="s">
          <Button
            variant="outline"
            textColor="error"
            onClick={onDelete}
            disabled={isLoading}
          >
            <Content id={ContentId.Delete} />
          </Button>
        </Stack>
      ) : (
        <span>&nbsp;</span>
      )}
      <Stack gap="s">
        <Button variant="outline" onClick={onClose} disabled={isLoading}>
          <Content id={ContentId.Cancel} />
        </Button>
        <Button onClick={onSubmitHandler} disabled={isLoading}>
          <Content id={ContentId.Save} />
        </Button>
      </Stack>
    </Stack>
  );
};

interface NotificationModalProps {
  notificationInfo?: EventNotification;
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationModal = ({
  notificationInfo,
  isOpen,
  onClose,
}: NotificationModalProps) => {
  const methods = useForm<EventNotification>({
    defaultValues: notificationInfo,
  });

  useEffect(() => {
    if (notificationInfo != null && methods.formState.defaultValues == null) {
      methods.reset(notificationInfo);
    }
  }, [methods, notificationInfo]);

  return (
    <FormProvider {...methods}>
      <NotificationModalContent
        isOpen={isOpen}
        onClose={onClose}
        isEditMode={
          notificationInfo?.id != null && notificationInfo.id !== EMPTY_GUID
        }
      />
    </FormProvider>
  );
};

export const NotificationModalContent = ({
  isOpen,
  onClose,
  isEditMode,
}: {
  isOpen: boolean;
  onClose: () => void;
  isEditMode: boolean;
}) => {
  const [modalOpen, toggleModalOpen] = useToggle(isOpen);

  const {
    formState: { errors },
    watch,
    register,
    clearErrors,
    setValue,
    reset,
  } = useFormContext<EventNotification>();

  const name = watch('name');
  const notificationType = watch('notificationType');
  const userIdTargets = watch('userIdTargets');
  const webhookTargets = watch('webhookTargets');
  const filters = watch('filters');

  const handleClose = () => {
    toggleModalOpen();
    onClose();
    reset(undefined);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={modalOpen}
      onClose={handleClose}
      unmountOnClose
      clickOutsideToClose
      centered
      size="md"
    >
      <ModalHeader onClose={handleClose}>
        <NotificationModalHeader isEditMode={isEditMode} />
      </ModalHeader>
      <ModalBody>
        <Stack className={styles.root} direction="column" gap="xl">
          <Stack direction="column">
            <Label.Root className={styles.input} htmlFor="name">
              <Content id={ContentId.Name} />
            </Label.Root>
            <PosFormField errors={errors.name?.message}>
              <PosTextField
                rootProps={{
                  state: getTextFieldState(errors.name),
                }}
                id="name"
                value={name}
                {...register('name')}
                onChange={(e) => {
                  clearErrors('name');
                  setValue('name', e.target.value);
                }}
              />
            </PosFormField>
          </Stack>

          <Stack direction="column" width="full">
            <Label.Root className={styles.input} htmlFor="notificationType">
              <Content id={ContentId.NotificationType} />
            </Label.Root>
            <PosFormField errors={errors.notificationType?.message}>
              <PosSelect
                id="notificationType"
                value={notificationType}
                valueOptionsContent={EVENT_NOTIFICATION_TYPE_TO_CID}
                style={{ width: '100%' }}
                onChange={(value) => {
                  clearErrors('notificationType');
                  setValue('notificationType', value as EventNotificationType);
                  if (value === EventNotificationType.Email) {
                    setValue('webhookTargets', []);
                    setValue('userIdTargets', []);
                  } else {
                    setValue('userIdTargets', []);
                    setValue('webhookTargets', [
                      {
                        id: -1,
                        name: '',
                        url: '',
                        authorizationHeader: '',
                      } as Webhook,
                    ]);
                  }
                }}
              />
            </PosFormField>
          </Stack>

          {notificationType === EventNotificationType.Webhook &&
            webhookTargets.map((webhook, index) => (
              <>
                <Stack direction="column">
                  <Label.Root className={styles.input} htmlFor="url">
                    <Content id={ContentId.WebhookUrl} />
                  </Label.Root>
                  <PosFormField
                    errors={errors.webhookTargets?.[index]?.url?.message}
                  >
                    <PosTextField
                      rootProps={{
                        state: getTextFieldState(
                          errors.webhookTargets?.[index]?.url
                        ),
                      }}
                      id="url"
                      type="url"
                      value={webhook.url ?? ''}
                      {...register(`webhookTargets.${index}.url`)}
                      onChange={(e) => {
                        clearErrors(`webhookTargets.${index}.url`);
                        setValue(`webhookTargets.${index}.url`, e.target.value);
                      }}
                    />
                  </PosFormField>
                </Stack>

                <Stack direction="column">
                  <Label.Root
                    className={styles.input}
                    htmlFor="authorizationHeader"
                  >
                    <Content id={ContentId.Headers} />
                  </Label.Root>
                  <PosFormField
                    errors={
                      errors.webhookTargets?.[index]?.authorizationHeader
                        ?.message
                    }
                  >
                    <PosTextArea
                      rootProps={{
                        state: getTextFieldState(
                          errors.webhookTargets?.[index]?.authorizationHeader
                        ),
                      }}
                      id="authorizationHeader"
                      value={webhook.authorizationHeader ?? ''}
                      onChange={(e) => {
                        clearErrors(
                          `webhookTargets.${index}.authorizationHeader`
                        );
                        setValue(
                          `webhookTargets.${index}.authorizationHeader`,
                          e.target.value
                        );
                      }}
                      rows={4}
                    />
                  </PosFormField>
                </Stack>
              </>
            ))}

          {notificationType === EventNotificationType.Email && (
            <Stack direction="column">
              <PosFormField
                label={<Content id={ContentId.Users} />}
                errors={errors.userIdTargets?.message}
                style={{ width: '100%' }}
              >
                <SellerAccountEmployeeMultiSelector
                  triggerProps={{ style: { width: '100%' } }}
                  values={userIdTargets ?? []}
                  enableEmptySelection={false}
                  placeholderText={ContentId.NoneSelected}
                  onChange={({ newValues }) => {
                    clearErrors('userIdTargets');
                    setValue('userIdTargets', newValues);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </PosFormField>
            </Stack>
          )}

          {/* TODO - Filters Section - https://thestubhub.atlassian.net/browse/SUP-1922 */}
          {!filters && (
            <FailureMessage message="Manually set-up filters is not yet supported.  Please create a notification from the Filter bar in Explore view." />
          )}
        </Stack>
      </ModalBody>
      <ModalFooter>
        <NotificationModalFooter
          onClose={handleClose}
          isEditMode={isEditMode}
        />
      </ModalFooter>
    </Modal>
  );
};
